import type { QuestKind, QuestTemplate, VerificationType } from "./types.js";

const bannedPunctuation = /[—:;]/;
const aiPhrases = [
  "share your thoughts",
  "dive into",
  "fellow community members",
  "spark a conversation",
  "engage with the community",
  "let's explore",
  "whether you're"
];

export interface QuestStyleResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export function validateQuestCopy(prompt: string): QuestStyleResult {
  const normalized = prompt.trim().toLowerCase();
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!prompt.trim()) errors.push("Quest text cannot be empty");
  if (bannedPunctuation.test(prompt)) errors.push("Quest text cannot use an em dash, colon, or semicolon");
  if (prompt.length > 180) errors.push("Quest text must stay under 180 characters");
  if (prompt.split(/[.!?]+/).filter(Boolean).length > 2) warnings.push("Keep the quest to one or two short sentences");
  for (const phrase of aiPhrases) {
    if (normalized.includes(phrase)) warnings.push(`Rewrite the phrase ${phrase}`);
  }
  if (/^[A-Z\s!]{12,}$/.test(prompt)) warnings.push("Avoid writing the whole quest in capital letters");
  return { valid: errors.length === 0, errors, warnings };
}

type QuestSeed = {
  prompt: string;
  channelGroup: string;
  verification?: VerificationType;
  kind?: QuestKind;
};

const seeds: QuestSeed[] = [
  { prompt: "What game do you wish you could play again for the first time?", channelGroup: "general" },
  { prompt: "Share one small win from your week.", channelGroup: "general" },
  { prompt: "What is something you learned recently that actually stuck with you?", channelGroup: "general" },
  { prompt: "What meal could you happily eat twice this week?", channelGroup: "general" },
  { prompt: "Tell us about the last thing that made you laugh harder than expected.", channelGroup: "general" },
  { prompt: "What is your go to background noise when you need to focus?", channelGroup: "general" },
  { prompt: "What is one thing you are looking forward to this weekend?", channelGroup: "general" },
  { prompt: "Pick a harmless hill you are willing to die on.", channelGroup: "general" },
  { prompt: "What is a skill you would love to learn if time was not an issue?", channelGroup: "general" },
  { prompt: "Post the last photo you took that has a story behind it.", channelGroup: "general", verification: "attachment" },
  { prompt: "What tiny purchase made your day better than it had any right to?", channelGroup: "general" },
  { prompt: "Name a song that can pull you out of a bad mood.", channelGroup: "general" },

  { prompt: "Drop the funniest clip on your camera roll. Keep it server safe.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Post a meme that perfectly describes your week.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Share a gaming clip where everything went wrong at once.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Find a clip that deserves more attention and tell us why.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Post a reaction image you use far too often.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Share the cleanest play you have captured this month.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Show us a screenshot that looks strange without context.", channelGroup: "clips", verification: "attachment" },
  { prompt: "Reply to someone else's clip with the moment that made you laugh.", channelGroup: "clips", verification: "reply" },

  { prompt: "Recommend a book you could not put down. Tell us what hooked you.", channelGroup: "books" },
  { prompt: "What fictional world would you visit for one week?", channelGroup: "books" },
  { prompt: "Name a book that surprised you in a good way.", channelGroup: "books" },
  { prompt: "What character did you dislike at first and understand later?", channelGroup: "books" },
  { prompt: "Share the book sitting at the top of your reading list.", channelGroup: "books" },
  { prompt: "What is the best opening chapter you remember reading?", channelGroup: "books" },
  { prompt: "Recommend a short book for someone getting back into reading.", channelGroup: "books" },
  { prompt: "Pick someone's recommendation and ask them one question about it.", channelGroup: "books", verification: "reply" },

  { prompt: "Recommend a game that respects your time.", channelGroup: "gaming" },
  { prompt: "What game has the best sound design you have heard?", channelGroup: "gaming" },
  { prompt: "Name a game you love despite one terrible mechanic.", channelGroup: "gaming" },
  { prompt: "What co op game should more people try?", channelGroup: "gaming" },
  { prompt: "Share your favorite character build from any game.", channelGroup: "gaming" },
  { prompt: "What game did you bounce off before it finally clicked?", channelGroup: "gaming" },
  { prompt: "Pick one game to keep installed forever.", channelGroup: "gaming" },
  { prompt: "What boss fight still lives in your head?", channelGroup: "gaming" },
  { prompt: "Recommend a game for a rainy weekend.", channelGroup: "gaming" },
  { prompt: "Tell us about a game that was better with friends.", channelGroup: "gaming" },
  { prompt: "What game soundtrack belongs in your regular music rotation?", channelGroup: "gaming" },
  { prompt: "Reply to a game recommendation with one thing you want to know about it.", channelGroup: "gaming", verification: "reply" },

  { prompt: "What is the most cursed Phasmophobia contract you have survived?", channelGroup: "phasmophobia" },
  { prompt: "Which ghost gives your group the most trouble?", channelGroup: "phasmophobia" },
  { prompt: "Share your best beginner tip for someone starting Phasmophobia.", channelGroup: "phasmophobia" },
  { prompt: "What evidence item do you trust least when the hunt starts?", channelGroup: "phasmophobia" },
  { prompt: "Tell us about the fastest contract that turned into chaos.", channelGroup: "phasmophobia" },
  { prompt: "Which map do you know well enough to navigate in the dark?", channelGroup: "phasmophobia" },
  { prompt: "What is your group's funniest failed ghost guess?", channelGroup: "phasmophobia" },
  { prompt: "Share a Phasmophobia clip that still makes your group laugh.", channelGroup: "phasmophobia", verification: "attachment" },
  { prompt: "Pick the equipment loadout you would keep if you could only bring three items.", channelGroup: "phasmophobia" },
  { prompt: "What house rule makes your contracts more fun?", channelGroup: "phasmophobia" },
  { prompt: "Reply to a new player with one tip you wish you had known.", channelGroup: "phasmophobia", verification: "reply" },
  { prompt: "Name the moment you knew a hunt was about to go badly.", channelGroup: "phasmophobia" },

  { prompt: "Need a squad tonight? Post what you are playing and when you will be on.", channelGroup: "lfg", verification: "voice_confirmed", kind: "lfg" },
  { prompt: "Invite someone new to join your next game session.", channelGroup: "lfg", verification: "peer", kind: "lfg" },
  { prompt: "Put together a group for a game nobody has played this week.", channelGroup: "lfg", verification: "voice_confirmed", kind: "lfg" },
  { prompt: "Join an open group and help choose what to play next.", channelGroup: "lfg", verification: "peer", kind: "lfg" },
  { prompt: "Post your timezone and the two nights you are usually free.", channelGroup: "lfg" },
  { prompt: "Ask for one more player instead of letting the plan quietly die.", channelGroup: "lfg", verification: "peer", kind: "lfg" },
  { prompt: "After a group session, thank someone who made it fun.", channelGroup: "lfg", verification: "reply", kind: "helpful" },
  { prompt: "Help someone find a game that fits their schedule tonight.", channelGroup: "lfg", verification: "peer", kind: "helpful" },

  { prompt: "What family tradition do you hope never disappears?", channelGroup: "lifestyle" },
  { prompt: "Share an easy dinner that saves you on busy nights.", channelGroup: "lifestyle" },
  { prompt: "What is one corner of your home that feels especially comfortable?", channelGroup: "lifestyle" },
  { prompt: "Name a hobby that helps you slow down.", channelGroup: "lifestyle" },
  { prompt: "What is the best advice a family member gave you?", channelGroup: "lifestyle" },
  { prompt: "Share a small routine that makes mornings easier.", channelGroup: "lifestyle" },
  { prompt: "What is your favorite way to spend an hour away from a screen?", channelGroup: "lifestyle" },
  { prompt: "Post something you made, fixed, cooked, or grew recently.", channelGroup: "lifestyle", verification: "attachment" },

  { prompt: "Answer a question that has been waiting without a reply.", channelGroup: "helpful", verification: "peer", kind: "helpful" },
  { prompt: "Welcome someone you have not talked with before.", channelGroup: "helpful", verification: "peer", kind: "helpful" },
  { prompt: "Find a recommendation request and give one useful reason for your pick.", channelGroup: "helpful", verification: "peer", kind: "helpful" },
  { prompt: "Thank someone for a post that made the server better today.", channelGroup: "helpful", verification: "reply", kind: "helpful" }
];

export const QUEST_TEMPLATES: QuestTemplate[] = seeds.map((seed, index) => {
  const kind = seed.kind ?? (index % 5 === 4 ? "bonus" : "main");
  const rewardCopper = kind === "main" ? 20 : kind === "bonus" ? 10 : kind === "helpful" ? 5 : 20;
  const projectEffort = kind === "main" ? 10 : kind === "bonus" ? 5 : kind === "helpful" ? 2 : 10;
  return {
    id: `quest-${String(index + 1).padStart(3, "0")}`,
    prompt: seed.prompt,
    channelGroup: seed.channelGroup,
    kind,
    rewardCopper,
    projectEffort,
    verification: seed.verification ?? "message",
    enabled: true
  };
});

export function selectDailyQuests(input: {
  seed: number;
  recentQuestIds?: string[];
  allowedChannelGroups?: string[];
}): QuestTemplate[] {
  const recent = new Set(input.recentQuestIds ?? []);
  const allowed = new Set(input.allowedChannelGroups ?? []);
  const candidates = QUEST_TEMPLATES.filter(
    (quest) => quest.enabled && !recent.has(quest.id) && (allowed.size === 0 || allowed.has(quest.channelGroup))
  );
  if (candidates.length < 2) throw new Error("Not enough eligible quests to schedule");
  const first = candidates[Math.abs(input.seed) % candidates.length]!;
  const remaining = candidates.filter((quest) => quest.id !== first.id && quest.channelGroup !== first.channelGroup);
  const second = remaining[Math.abs(input.seed * 31 + 7) % remaining.length] ?? candidates.find((quest) => quest.id !== first.id)!;
  return [
    { ...first, kind: "main", rewardCopper: 20, projectEffort: 10 },
    { ...second, kind: "bonus", rewardCopper: 10, projectEffort: 5 }
  ];
}
